// ── Generic API client ────────────────────────────────────────────────────────
//
// Migrated from src/api.js — same TTL cache + write guard logic,
// now TypeScript and using the auth context's getToken().

type TokenGetter = () => Promise<string>;

let _getToken: TokenGetter = async () => '';

/** Call once at app init to wire up the token getter */
export function setTokenGetter(fn: TokenGetter) {
  _getToken = fn;
}

// ── In-memory TTL cache ───────────────────────────────────────────────────────
interface CacheEntry<T> { t: number; v: T }
const _cache = new Map<string, CacheEntry<unknown>>();

function cacheGet<T>(key: string, ttlMs: number): T | null {
  const hit = _cache.get(key) as CacheEntry<T> | undefined;
  if (!hit) return null;
  if (Date.now() - hit.t > ttlMs) { _cache.delete(key); return null; }
  return hit.v;
}

function cacheSet(key: string, v: unknown) {
  _cache.set(key, { t: Date.now(), v });
}

export function invalidateCache(prefix?: string) {
  if (!prefix) { _cache.clear(); return; }
  for (const k of _cache.keys()) if (k.startsWith(prefix)) _cache.delete(k);
}

// ── Fetch helper ──────────────────────────────────────────────────────────────

export interface FetchOpts extends RequestInit {
  cacheTtlMs?: number;
  /** Override — skip write-permission guard */
  skipWriteGuard?: boolean;
  /** Extra headers (merged) */
  extraHeaders?: Record<string, string>;
}

export async function apiFetch<T = unknown>(
  url: string,
  opts: FetchOpts = {}
): Promise<T> {
  const method = (opts.method ?? 'GET').toUpperCase();
  const isWrite = method !== 'GET' && method !== 'HEAD';

  const { cacheTtlMs, skipWriteGuard: _s, extraHeaders, ...init } = opts;

  // Cache check for GETs
  const cacheKey = cacheTtlMs ? `${method}:${url}` : null;
  if (cacheKey && !isWrite) {
    const hit = cacheGet<T>(cacheKey, cacheTtlMs!);
    if (hit !== null) return hit;
  }

  const token = await _getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extraHeaders,
    ...(init.headers as Record<string, string> | undefined),
  };

  const res = await fetch(url, { ...init, headers });

  // Invalidate cache after writes
  if (isWrite) invalidateCache();

  if (!res.ok) {
    const err = await res.json().catch(() => ({ errors: [{ message: res.statusText }] }));
    const msg =
      (err as { errors?: { message: string }[] }).errors?.[0]?.message ??
      (err as { detail?: string }).detail ??
      res.statusText;
    throw new Error(msg);
  }

  const data: T = res.status === 204 ? (null as T) : await res.json();

  if (cacheKey && data !== null) cacheSet(cacheKey, data);
  return data;
}

// ── Blob fetch (for PDF downloads) ───────────────────────────────────────────

export async function apiFetchBlob(
  url: string,
  opts: FetchOpts = {}
): Promise<Blob> {
  const { cacheTtlMs: _, skipWriteGuard: _s, extraHeaders, ...init } = opts;
  const token = await _getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extraHeaders,
    ...(init.headers as Record<string, string> | undefined),
  };
  const res = await fetch(url, { ...init, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ errors: [{ message: res.statusText }] }));
    const msg = (err as { errors?: { message: string }[] }).errors?.[0]?.message ?? res.statusText;
    throw new Error(msg);
  }
  return res.blob();
}

// ── Directus helpers — ΑΦΑΙΡΕΘΗΚΑΝ 27/09/2026 ─────────────────────────────────
//
// Εδώ υπήρχε `directusFetch(url, staticToken)`: καλούσε το Directus απευθείας
// από τον browser με στατικό token. Το token έπρεπε να ζει κάπου στο frontend
// για να περαστεί εδώ — και ζούσε σε σταθερά του `src`, άρα κατέβαινε σε κάθε
// επισκέπτη μέσα στο bundle.
//
// Η βοηθητική αφαιρέθηκε μαζί με τα tokens, ώστε να μην μπορεί κανείς να την
// ξαναχρησιμοποιήσει κατά λάθος. Κλήσεις προς Directus γίνονται μέσω του
// identity service με `apiFetch` (Keycloak token του χρήστη).
